import React, { useState } from "react";
import { Table, Button } from "react-bootstrap";
import axios from "axios";
import { FaChevronRight, FaChevronDown, FaChevronUp } from "react-icons/fa";

/* =========================
   Common compact cell style
   (KEEP SMALL like baseline)
========================= */
const compactCell = {
  padding: "2px 4px",
  verticalAlign: "middle",
};

/* =========================
   Sticky header cell style
========================= */
const stickyTh = {
  ...compactCell,
  position: "sticky",
  top: 0,
  background: "#f8f9fa",
  zIndex: 2,
  boxShadow: "0 2px 2px -1px rgba(0,0,0,0.08)",
};

const ReconSummaryGrid = ({
  summary = [],
  onSelect,                // View -> MatchResultsGrid (context)
  onVendorSummarySelect,   // ✅ NEW (does NOT replace onSelect)
  entityCode,              // OPTIONAL (preferred): pass from filters
  processMonth,            // OPTIONAL (preferred): pass from filters
  tolerance = 1,           // OPTIONAL: used for backend vendor summary
  vendorSummaryUrl    = "/api/gst2b/getVendorReconMatchedSummary", // API endpoint
  onApproveVendor,         // OPTIONAL callback
  onUnmatchVendor,         // OPTIONAL callback
}) => {
  const rows = Array.isArray(summary) ? summary : [];

  /* Section-level collapse */
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  /* Accordion: only one expanded parent row at a time */
  const [expandedIndex, setExpandedIndex] = useState(null);

  /* Cache vendor summaries by stable composite key */
  const [detailsCache, setDetailsCache] = useState({});

  /* Loading key for spinner */
  const [loadingKey, setLoadingKey] = useState(null);

  const isOnlyType = (matchType = "") =>
    ["ONLY_IN_ERP", "ONLY_IN_2B"].includes((matchType || "").toUpperCase());

  const canUnmatch = (matchType = "") =>
    ["GROUP", "FUZZY", "RELAXED"].some((k) =>
      (matchType || "").toUpperCase().includes(k)
    );

  /* Resolve screen context (prefer props, fallback to row fields) */
  const resolveContext = (row) => {
    const ec = entityCode ?? row?.ENTITY_CODE ?? row?.entity_code ?? null;
    const pm = processMonth ?? row?.PROCESS_MONTH ?? row?.process_month ?? null;
    return { entity_code: ec, process_month: pm };
  };

  /* Stable cache key so re-expand does NOT refetch */
  const getCacheKey = (row) => {
    const { entity_code, process_month } = resolveContext(row);
    return `${(row?.MATCH_TYPE || "").toUpperCase()}__${entity_code || ""}__${process_month || ""}`;
  };

  /* Expand handler: accordion + lazy load + cache */
  const toggleRow = (idx, row) => {
    if (isOnlyType(row.MATCH_TYPE)) return;
  
    // Accordion behavior (unchanged)
    if (expandedIndex === idx) {
      setExpandedIndex(null);
      return;
    }
  
    setExpandedIndex(idx);
  
    // Resolve context
    const { entity_code, process_month } = resolveContext(row);
    if (!entity_code || !process_month) return;
  
    // 🔥 NEW: raise event to workspace
    onVendorSummarySelect &&
      onVendorSummarySelect({
        matchType: row.MATCH_TYPE,
        entity_code,
        process_month,
      });
  };
    
  return (
    <div style={{ marginTop: "4px" }}>
      {/* =========================
         Header bar
      ========================= */}
      <div
        style={{
          background: "#f1f1f1",
          border: "1px solid #ddd",
          borderRadius: "4px 4px 0 0",
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "0.72rem",
          fontWeight: 500,
        }}
      >
        <span>Reconciliation Summary</span>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setSectionCollapsed((c) => !c)}
          style={{
            fontSize: "0.65rem",
            padding: "1px 6px",
            height: "22px",
            lineHeight: "0.9rem",
          }}
        >
          {sectionCollapsed ? (
            <>
              <FaChevronDown /> Show
            </>
          ) : (
            <>
              <FaChevronUp /> Hide
            </>
          )}
        </Button>
      </div>

      {/* =========================
         Scrollable table wrapper
      ========================= */}
      {!sectionCollapsed && (
        <div
          style={{
            maxHeight: "220px",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #ddd",
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
          }}
        >
          <Table
            bordered
            hover
            size="sm"
            style={{
              fontSize: "0.68rem",
              lineHeight: "0.9rem",
              margin: 0,
            }}
          >
            {/* Sticky Header */}
            <thead>
              <tr>
                <th style={stickyTh}>Match Type</th>
                <th className="text-end" style={stickyTh}>Records</th>
                <th className="text-end" style={stickyTh}>2B Taxable</th>
                <th className="text-end" style={stickyTh}>ERP Assessable</th>
                <th className="text-end" style={stickyTh}>2B IGST</th>
                <th className="text-end" style={stickyTh}>ERP IGST</th>
                <th className="text-end" style={stickyTh}>2B CGST</th>
                <th className="text-end" style={stickyTh}>ERP CGST</th>
                <th className="text-end" style={stickyTh}>2B SGST</th>
                <th className="text-end" style={stickyTh}>ERP SGST</th>
                <th className="text-end" style={stickyTh}>Variance</th>
                <th style={{ ...stickyTh, width: "55px" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, idx) => {
                const taxable2b = Number(row.TAXABLE_VALUE_2B || 0);
                const taxableErp = Number(row.ASSESABLE_VALUE_ERP || 0);
                const variance = taxable2b - taxableErp;

                const onlyType = isOnlyType(row.MATCH_TYPE);
                const isExpanded = expandedIndex === idx;

                const cacheKey = getCacheKey(row);
                const vendorRows = detailsCache[cacheKey] || [];
                const { entity_code, process_month } = resolveContext(row);
                const contextMissing = !entity_code || !process_month;

                return (
                  <React.Fragment key={idx}>
                    {/* Main summary row */}
                    <tr style={{ height: "22px", background: "#f9f9f9" }}>

                      <td style={compactCell}>{row.MATCH_TYPE}</td>

                      <td className="text-end" style={compactCell}>
                        {row.REC_CNT}
                      </td>

                      <td className="text-end" style={compactCell}>
                        {taxable2b.toFixed(2)}
                      </td>
                      <td className="text-end" style={compactCell}>
                        {taxableErp.toFixed(2)}
                      </td>

                      <td className="text-end" style={compactCell}>
                        {Number(row.IGST_2B || 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={compactCell}>
                        {Number(row.ERP_IGST || 0).toFixed(2)}
                      </td>

                      <td className="text-end" style={compactCell}>
                        {Number(row.CGST_2B || 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={compactCell}>
                        {Number(row.ERP_CGST || 0).toFixed(2)}
                      </td>

                      <td className="text-end" style={compactCell}>
                        {Number(row.SGST_2B || 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={compactCell}>
                        {Number(row.ERP_SGST || 0).toFixed(2)}
                      </td>

                      <td
                        className="text-end"
                        style={{
                          ...compactCell,
                          color: Math.abs(variance) > 1 ? "red" : "black",
                          fontWeight: Math.abs(variance) > 1 ? 600 : 400,
                        }}
                      >
                        {variance.toFixed(2)}
                      </td>

                      {/* Action: View (NOT for ONLY_* ) */}
                      <td className="text-center" style={compactCell}>
                        {!onlyType && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            style={{
                              fontSize: "0.63rem",
                              padding: "1px 4px",
                              height: "22px",
                            }}
                            onClick={() => { 
                              // 🔥 OnSelect event                              
                              onSelect && onSelect(row.MATCH_TYPE);
                              // 🔥 NEW: raise vendor-summary context to workspace
                              onVendorSummarySelect &&
                              onVendorSummarySelect({
                                matchType: row.MATCH_TYPE,
                                entity_code,
                                process_month,
                              });

                            }}
                          >
                            View
                          </Button>
                        )}
                      </td>
                    </tr>

                    {/* Expanded vendor summary grid */}
                    {isExpanded && !onlyType && (
                      <tr>
                        <td colSpan={13} style={{ fontSize: "0.65rem", padding: "4px" }}>
                          Vendor summary moved to <b>Vendor Summary</b> tab
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ReconSummaryGrid;
